import { FC, useCallback } from 'react';
import { useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import TextField from 'components/TextField';

import { useDialog } from 'components/Dialog';
import { useErrorHandler } from 'lib';

import { WhatToSeeListAddMutation } from '__generated__/WhatToSeeListAddMutation.graphql';

type FormFields = {
  name: string;
};

type Props = {
  connectionID: string;
};

const initial = {
  name: '',
};

const WhatToSeeListAdd: FC<Props> = ({ connectionID }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const [add, adding] = useMutation<WhatToSeeListAddMutation>(graphql`
    mutation WhatToSeeListAddMutation(
      $input: CreateWhatToSeeInput!
      $connections: [ID!]!
    ) {
      createWhatToSee(input: $input) {
        node
          @prependNode(
            connections: $connections
            edgeTypeName: "WhatToSeeEdge"
          ) {
          ...WhatToSeeListRow_whatToSee
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: { connections: [connectionID], input },
        ...errorHandler({
          onCompleted: () => {
            close();
          },
        }),
      });
    },
    [add, close, connectionID, errorHandler]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas straipsnis</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują straipsnį. Naudjami kelionės pasiūlymų aprašyme.
          </DialogContentText>

          <Stack spacing={4}>
            <TextField autoFocus name="name" label="Pavadinimas" fullWidth />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default WhatToSeeListAdd;
